import React, { useEffect, useRef, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import styled from "styled-components";

import { ReactComponent as AerovectLogoBlue } from "../../assets/aerovect-logo-blue.svg";
import type { SignInOptions, SignInPath } from "../../auth/fleetAuth";
import { doorForEmail } from "../../auth/doorForEmail";
import { AUTH_PROVIDER } from "../../utils/devFlags";

/**
 * The only thing an unauthenticated visitor sees.
 *
 * It asks one question -- which person are you? -- because the two
 * populations have strictly separate ways in and the identity provider's own
 * page cannot say so: Cognito's managed login renders a Google button and a
 * password form on one card under labels AWS does not let us edit ("With
 * other accounts", "With existing account"). Routing here, before that card,
 * is what makes each door lead somewhere that works:
 *
 *   employee  -> straight to Google Workspace; the card is never shown
 *   client    -> the card, with the email + password form first
 *
 * THE CLIENT DOOR ASKS FOR THE ADDRESS BEFORE LEAVING. Cognito's card has no
 * way back: its only link is "Forgot your password?", and managed login cannot
 * carry custom text, so an employee who picked the wrong door was stranded on
 * a password form that would never accept them, with only the browser's Back
 * button -- which works, but nobody is told so -- to rescue them (SW-8056). So
 * this page asks for the email first, here, where it can still act on the
 * answer: a corporate address is routed through the employee door instead, any
 * other goes to the card with the address prefilled (`login_hint`), and a Back
 * link returns to the two doors. The card is reached only by someone it can
 * sign in.
 *
 * The employee door is deliberately NOT Google-branded. "Continue with
 * Google" reads as an open invitation to anyone with a Google account, and a
 * personal one is rejected server-side; "your AeroVect account" says who it
 * is for. Where it goes next is the provider's business, not the label's.
 *
 * ONE DOOR ON AUTH0, TWO ON COGNITO, and the reason is that the choice is only
 * real on one of them. The doors exist because the two populations open
 * different Cognito app clients: `fleet_console` sent to Google, and
 * `fleet_console_external`, whose card offers no Google button at all. Auth0
 * has a single client and its Universal Login shows its own page, so
 * `login()` ignores the SignInPath there (see fleetAuth.tsx) and both buttons
 * do exactly the same thing.
 *
 * Asking a question whose answers are identical is worse than not asking, so a
 * one-door build does not ask: RequireAuth starts that sign-in itself and this
 * page is never seen (SW-7116). It still renders the single button in one
 * case, and it is the case that matters -- after a failed callback, where an
 * automatic retry is what looped (SW-6866). Then the button is the person's
 * retry.
 *
 * The two doors and the provider flip arrive together, which is the only
 * moment the second door leads anywhere different.
 *
 * Deliberately presentational: no auth hook, no redirect of its own. The gate
 * (RequireAuth) decides when this renders and what `error` says; this page
 * only reports the click and holds the "redirecting" state until the browser
 * actually leaves, so a slow redirect never reads as "nothing happened".
 */
export interface SignInProps {
  /**
   * Start the chosen sign-in. Rejects if the redirect could not even begin.
   * `options` is present only from the email step, carrying the address.
   */
  onChoose: (path: SignInPath, options?: SignInOptions) => Promise<void> | void;
  /** Already in plain words -- see signInError.ts. */
  error?: string | null;
  /**
   * Show both doors, or one. Defaults to the build's provider and is a prop so
   * a test can render either without reloading modules. See the note above for
   * why one door is right on Auth0.
   */
  showBothDoors?: boolean;
}

export const EMPLOYEE_BUTTON = "Sign in with your AeroVect account";
export const CLIENT_BUTTON = "Sign in with email and password";
/** The single button an Auth0 build shows, where the doors are the same door. */
export const SINGLE_BUTTON = "Sign in";

// The email step behind the client door.
export const EMAIL_LABEL = "Email address";
export const CONTINUE_BUTTON = "Continue";
export const BACK_BUTTON = "Back to sign-in options";
export const EMPTY_EMAIL = "Enter your email address.";
export const BAD_EMAIL = "Enter a complete email address, like name@host.com.";
/** Shown while a corporate address is being sent through the employee door. */
export const EMPLOYEE_ROUTED =
  "That’s an AeroVect account — taking you to the employee sign-in.";

/** Two doors only where they lead somewhere different. See the note above. */
export const SHOWS_BOTH_DOORS = AUTH_PROVIDER === "cognito";

const SignIn = ({
  onChoose,
  error,
  showBothDoors = SHOWS_BOTH_DOORS,
}: SignInProps) => {
  const [busy, setBusy] = useState<SignInPath | null>(null);
  // "doors" is the question; "email" is the client door's step before leaving.
  const [step, setStep] = useState<"doors" | "email">("doors");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const clientDoorRef = useRef<HTMLButtonElement>(null);
  // Set by Back, so focus lands on the door the person came through rather
  // than at the top of the document.
  const returnToClientDoor = useRef(false);

  useEffect(() => {
    if (step === "doors" && returnToClientDoor.current) {
      returnToClientDoor.current = false;
      clientDoorRef.current?.focus();
    }
  }, [step]);

  // Back from the identity provider's page restores this one from the
  // back/forward cache with its state intact -- including `busy`, which is
  // deliberately kept after a successful redirect so the button does not
  // flicker before the browser leaves. On that restore nothing is redirecting
  // any more, so open both doors again; otherwise the page is dead until a
  // manual reload.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setBusy(null);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const start = async (path: SignInPath, options?: SignInOptions) => {
    if (busy) return;
    setBusy(path);
    try {
      // Exactly the arguments the step produced: the doors report a path
      // alone, the email step adds the address.
      await (options ? onChoose(path, options) : onChoose(path));
      // On success the page is navigating away; keep the busy state so the
      // button does not flicker back before the browser leaves.
    } catch {
      // The gate owns the message; this page only has to become clickable.
      setBusy(null);
    }
  };

  const choose = (path: SignInPath) => {
    // The client door asks before it leaves; see the note above.
    if (path === "client" && showBothDoors) {
      setStep("email");
      return;
    }
    void start(path);
  };

  const back = () => {
    setEmail("");
    setEmailError(null);
    returnToClientDoor.current = true;
    setStep("doors");
  };

  const submitEmail = (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (!email.trim()) {
      setEmailError(EMPTY_EMAIL);
      return;
    }
    const door = doorForEmail(email);
    if (!door) {
      setEmailError(BAD_EMAIL);
      return;
    }
    setEmailError(null);
    void start(door.path, { loginHint: door.email });
  };

  return (
    <Page>
      <Card aria-labelledby="signin-title">
        <Logo>
          <AerovectLogoBlue role="img" aria-label="AeroVect" />
        </Logo>
        <Title id="signin-title">Sign in to Fleet Console</Title>

        {error && <Alert role="alert">{error}</Alert>}

        {showBothDoors && step === "email" ? (
          <form onSubmit={submitEmail} noValidate>
            <Group>
              <Who>External users</Who>
              <Label htmlFor="signin-email">{EMAIL_LABEL}</Label>
              <EmailInput
                id="signin-email"
                type="email"
                name="email"
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="name@host.com"
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                disabled={busy !== null}
                aria-invalid={emailError ? true : undefined}
                aria-describedby={emailError ? "signin-email-error" : undefined}
              />
              {emailError && (
                <FieldError id="signin-email-error" role="alert">
                  {emailError}
                </FieldError>
              )}
              <PrimaryButton type="submit" aria-busy={busy !== null}>
                {busy !== null ? (
                  <>
                    <CircularProgress size={16} color="inherit" />
                    Redirecting…
                  </>
                ) : (
                  CONTINUE_BUTTON
                )}
              </PrimaryButton>
              {/* A corporate address is not refused, it is redirected -- and
                  the redirect goes somewhere the person did not click, so it
                  says where. */}
              {busy === "employee" && (
                <Routed role="status">{EMPLOYEE_ROUTED}</Routed>
              )}
            </Group>

            <BackLink type="button" onClick={back} disabled={busy !== null}>
              <span aria-hidden="true">‹</span> {BACK_BUTTON}
            </BackLink>
          </form>
        ) : showBothDoors ? (
          <>
            <Group>
              <Who>AeroVect employees</Who>
              <PrimaryButton
                type="button"
                onClick={() => choose("employee")}
                disabled={busy === "client"}
                aria-busy={busy === "employee"}
              >
                {busy === "employee" ? (
                  <>
                    <CircularProgress size={16} color="inherit" />
                    Redirecting…
                  </>
                ) : (
                  EMPLOYEE_BUTTON
                )}
              </PrimaryButton>
            </Group>

            <Rule aria-hidden="true" />

            <Group>
              <Who>External users</Who>
              <SecondaryButton
                ref={clientDoorRef}
                type="button"
                onClick={() => choose("client")}
                disabled={busy === "employee"}
              >
                {CLIENT_BUTTON}
              </SecondaryButton>
            </Group>
          </>
        ) : (
          <Group>
            <PrimaryButton
              type="button"
              onClick={() => choose("employee")}
              aria-busy={busy !== null}
            >
              {busy !== null ? (
                <>
                  <CircularProgress size={16} color="inherit" />
                  Redirecting…
                </>
              ) : (
                SINGLE_BUTTON
              )}
            </PrimaryButton>
          </Group>
        )}
      </Card>
    </Page>
  );
};

// Same stage as the Cognito card that follows for clients (platform_auth
// managed_login_settings.json: navy page, white 12px card, blue action), so
// the console page and the hosted card read as one flow, not two products.
const Page = styled.main`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  background: ${(props) => props.theme.colors.navyBlue};
  font-family:
    Inter,
    system-ui,
    -apple-system,
    "Segoe UI",
    sans-serif;
`;

const Card = styled.section`
  width: 400px;
  max-width: 100%;
  background: ${(props) => props.theme.colors.paper};
  color: #222a35;
  border-radius: 12px;
  padding: 32px 28px 28px;
  box-shadow: 0 24px 60px rgba(0, 17, 61, 0.35);
  box-sizing: border-box;

  @media (max-width: 400px) {
    padding: 28px 20px 24px;
  }
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 22px;

  svg {
    height: 40px;
    width: auto;
  }
`;

const Title = styled.h1`
  margin: 0 0 24px;
  font-size: 22px;
  font-weight: ${(props) => props.theme.fontWeight.bold};
  letter-spacing: -0.01em;
  line-height: 1.2;
`;

const Alert = styled.div`
  margin: 0 0 20px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.danger};
  background: #fef2f2;
  color: ${(props) => props.theme.colors.danger};
  font-size: 13.5px;
  line-height: 1.45;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Who = styled.div`
  font-size: 11.5px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #65727c;
`;

const BaseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: 1px solid transparent;
  font: inherit;
  font-size: 15px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.blue};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
`;

const PrimaryButton = styled(BaseButton)`
  background: ${(props) => props.theme.colors.blue};
  color: ${(props) => props.theme.colors.white};

  &:hover:not(:disabled),
  &[aria-busy="true"] {
    background: ${(props) => props.theme.colors.blueHover};
  }
`;

const SecondaryButton = styled(BaseButton)`
  background: transparent;
  color: ${(props) => props.theme.colors.blueHover};
  border-color: #c9d4dc;

  &:hover:not(:disabled) {
    border-color: ${(props) => props.theme.colors.blueHover};
    background: #f2f8fd;
  }
`;

const Rule = styled.hr`
  margin: 22px 0;
  border: 0;
  height: 1px;
  background: #e2e9ef;
`;

const Label = styled.label`
  font-size: 13.5px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  color: #41505c;
`;

// The same field the Cognito card draws next (managed_login_settings.json:
// 8px radius, #75868f border, #65727c placeholder, blue focus), so typing the
// address here and seeing it there reads as one form in two places.
const EmailInput = styled.input`
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #75868f;
  background: ${(props) => props.theme.colors.white};
  color: #222a35;
  font: inherit;
  font-size: 15px;

  &::placeholder {
    color: #65727c;
    font-style: italic;
  }
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.blue};
    box-shadow: 0 0 0 3px rgba(3, 148, 232, 0.25);
  }
  &[aria-invalid="true"] {
    border-color: ${(props) => props.theme.colors.danger};
  }
  &:disabled {
    opacity: 0.55;
  }
`;

const FieldError = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: ${(props) => props.theme.colors.danger};
`;

const Routed = styled.p`
  margin: 2px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #41505c;
`;

const BackLink = styled.button`
  display: block;
  width: 100%;
  margin-top: 18px;
  padding: 6px 0;
  background: none;
  border: none;
  font: inherit;
  font-size: 14px;
  font-weight: ${(props) => props.theme.fontWeight.semibold};
  color: ${(props) => props.theme.colors.blueHover};
  cursor: pointer;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.blue};
    outline-offset: 2px;
    border-radius: 6px;
  }
  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
`;

export default SignIn;
